import React from 'react';
import FuzzySearch from 'fuzzy-search';
import {
  PropertyConfig,
  resourceAgnosticPropertyKeys,
  Link,
} from '../../utils/FhirResourceDisplayPropertyConfig/propertyConfig';
import { DataSourceType } from '../../../util/types';
import { APIResource } from '../../../../../util/fhirResourceParsers/FhirPropertyParser';
import JMESDisplay from '../../../common/JMESDisplay';
import { ConfiguredMomentFormat } from '../../../util/momentFormats';
import {
  TableConfiguration,
  TableSortState,
  TableSortClickProps,
} from '@lifeomic/chroma-react/components/TableModule';
import { defaultSortState } from './getDefaultColumnSort';
import TableLink from '../TableLink';
import JMESParser from '../../../../../util/JMESParser';
import { FormatMessageSignature } from '../../../../../util/JMESParser/jmesFhirTypes';
import Favorite from '../../../common/Favorite';

type SortCompareTypes = 'number' | 'boolean' | 'string' | 'object';
type SortTypes = number | boolean | string | object;

// order types by number -> boolean -> string
const typeToCompareVal: { [key in SortCompareTypes]: number } = {
  number: 0,
  boolean: 1,
  string: 2,
  object: 3, // null values
};

const columnSortFunc = (val1: SortTypes, val2: SortTypes) => {
  const type1 = typeof val1 as SortCompareTypes;
  const type2 = typeof val2 as SortCompareTypes;

  if (type1 !== type2) {
    return typeToCompareVal[type1] - typeToCompareVal[type2];
  } else if (type1 === 'number') {
    return (val1 as number) - (val2 as number);
  } else if (type1 === 'boolean') {
    return val1 === val2 ? 0 : val1 ? 0 : 1;
  } else if (type1 === 'string') {
    return (val1 as string).localeCompare(val2 as string);
  } else if (val1 === null && val2 !== null) {
    return 1;
  } else if (val1 !== null && val2 === null) {
    return -1;
  } else if (val1 === null && val2 === null) {
    return 0;
  } else {
    console.warn(
      `Unable to sort values ${JSON.stringify(val1)} and ${JSON.stringify(
        val2,
      )}`,
    );
    return 0;
  }
};

export type SetSortState = (sortState: TableSortState) => void;

const getCellContentRenderer =
  (columnIndex: number, isInEditMode: boolean, onFavoriteUpdate: () => void) =>
  (row: any) => {
    const rowData = row.item as ParsedDisplayValue[];
    const cellData = rowData[columnIndex];
    if (!cellData) return null;
    if (cellData.isLink) {
      return (
        <TableLink
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          url={cellData.link.url}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          contentType={cellData.link.contentType}
        />
      );
    } else if (cellData.jmesParser) {
      return (
        <JMESDisplay
          color="default"
          jmesParser={cellData.jmesParser}
          displayNotAvailable
          isInEditMode={isInEditMode}
        />
      );
    } else if (cellData.isFavorite) {
      return (
        <Favorite
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          key={cellData.sourceResource.id}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          resource={cellData.sourceResource}
          onFavoriteUpdate={onFavoriteUpdate}
        />
      );
    } else {
      return cellData.displayValue;
    }
  };

export type ParsedDisplayValue = {
  displayValue: any;
  isLink: boolean;
  link?: Link;
  jmesParser?: JMESParser;
  sourceResource: fhir.Resource | null;
  isFavorite?: boolean;
};

export type ParseResult = {
  rows: { item: ParsedDisplayValue[]; rowMeta?: unknown }[];
  config: TableConfiguration[];
};

export const emptyParseResult: ParseResult = { rows: [], config: [] };

const parse = (
  dataSource: DataSourceType,
  configuredDisplayColumns: PropertyConfig[] = [],
  responses: APIResource[] = [],
  sort: TableSortState,
  setSortState: SetSortState,
  momentFormat: ConfiguredMomentFormat,
  formatMessage: FormatMessageSignature,
  searchValue: string,
  isInEditMode: boolean,
  cellClassName: string,
  onFavoriteUpdate: () => void,
): ParseResult => {
  if (!dataSource) return emptyParseResult;

  const copiedResponses = [...responses];

  const { sortDirection, sortKey } = sort || defaultSortState;

  const sortColumnConfig =
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    sortKey !== null && configuredDisplayColumns[sortKey];

  // sort
  if (sortColumnConfig && sortDirection !== null) {
    const orderMult = sortDirection === 'asc' ? 1 : -1;
    copiedResponses.sort((response1, response2) => {
      const val1 = sortColumnConfig.parserGroup.getSortValue(response1);
      const val2 = sortColumnConfig.parserGroup.getSortValue(response2);

      return orderMult * columnSortFunc(val1, val2);
    });
  }

  // get display value for each cell
  const rows: any[] = copiedResponses.map(response => {
    const parsedDisplayValues: ParsedDisplayValue[] = [];
    configuredDisplayColumns.forEach(config => {
      if (config.key === resourceAgnosticPropertyKeys.jmesColumn) {
        const resource = (response || ({} as APIResource)).resource;
        const jmesParser = new JMESParser(
          resource,
          config.jmesPath,
          config.jmesResultParser,
          momentFormat,
          formatMessage as FormatMessageSignature,
        );
        parsedDisplayValues.push({
          displayValue: jmesParser.parse(),
          isLink: false,
          jmesParser,
          sourceResource: null,
        });
      } else if (config.isFavorite) {
        parsedDisplayValues.push({
          isLink: false,
          isFavorite: true,
          sourceResource: (response || ({} as APIResource)).resource,
          displayValue: '',
        });
      } else {
        parsedDisplayValues.push({
          displayValue: config.parserGroup.getDisplayValue(response),
          isLink: config.isLink,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          link: config.isLink ? config.getLink(response) : null,
          sourceResource: response?.resource || null,
        });
      }
    });
    return {
      item: parsedDisplayValues,
      rowMeta: response,
    };
  });

  let searchFilteredRows = rows;

  // filter by search
  if (searchValue && rows && rows.length) {
    const searchKeys = Object.keys(rows[0])
      .filter(indexKey => indexKey !== 'rowMeta')
      .map(indexKey => `${indexKey}.displayValue`);
    const searcher = new FuzzySearch(rows, searchKeys, { sort: true });
    searchFilteredRows = searcher.search(searchValue);
  }

  // get table header
  const config: TableConfiguration[] = configuredDisplayColumns.map(
    (column: PropertyConfig, index: number) => {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const onSort: (sortProps: TableSortClickProps) => void =
        column.disableSort
          ? null
          : (sortProps: TableSortClickProps) => {
              setSortState({
                sortKey: sortProps.index,
                sortDirection: sortProps.sortDirection,
              });
            };

      return {
        header: {
          label:
            column.configuredLabel ||
            (column.staticLabel && formatMessage(column.staticLabel)) ||
            '',
          onSort,
          className: cellClassName,
        },
        cell: {
          content: getCellContentRenderer(
            index,
            isInEditMode,
            onFavoriteUpdate,
          ),
          className: cellClassName,
        },
      } as TableConfiguration;
    },
  );
  return { config, rows: searchFilteredRows };
};

export default parse;
